import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Terminal, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@acehack.io");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const res = await login(email.trim(), password);
    setBusy(false);
    if (res.ok) navigate("/app");
    else setError(res.error);
  };

  return (
    <div className="min-h-screen bg-black grid grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:block relative overflow-hidden border-r border-neutral-900">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/bf72d466-65db-4239-bf11-d117be4e8f6f/images/6408ec56c4294cc86104e3ca47319840b2030d254bef37de9e2a6e5aa464d94a.png"
          alt="poker chip on motherboard"
          className="absolute inset-0 w-full h-full object-cover opacity-90"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-black/10 to-black/60" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 border border-red-600/70 flex items-center justify-center bg-black">
              <Terminal size={16} className="text-red-500" />
            </div>
            <div>
              <div className="font-serif text-2xl tracking-tight">AceHack</div>
              <div className="label-mono">// suite.gate</div>
            </div>
          </div>
          <div>
            <div className="label-mono mb-3 text-green-400">{">"} session.authenticate</div>
            <h2 className="font-serif text-4xl lg:text-5xl tracking-tight leading-tight">
              The house never bluffs.
              <br />
              <span className="glow-green italic">Login.</span>
            </h2>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 lg:p-16 felt relative">
        <div className="scanlines absolute inset-0 pointer-events-none" />
        <form
          onSubmit={handleSubmit}
          data-testid="login-form"
          className="relative z-10 panel panel-accent-red w-full max-w-md p-8 deal"
        >
          <div className="mb-8">
            <div className="label-mono mb-2 text-green-400">{"//"} access</div>
            <h1 className="font-serif text-3xl tracking-tight">Sit at the table</h1>
            <p className="text-sm text-neutral-400 mt-2">Enter your credentials to deal in.</p>
          </div>

          <label className="block mb-5">
            <span className="label-mono block mb-2">Email</span>
            <input
              data-testid="login-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-shell w-full px-3 py-3 font-jet text-sm"
              placeholder="you@example.com"
            />
          </label>

          <label className="block mb-6">
            <span className="label-mono block mb-2">Password</span>
            <div className="relative">
              <input
                data-testid="login-password-input"
                type={showPassword ? "text" : "password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-shell w-full px-3 py-3 pr-10 font-jet text-sm"
                placeholder="••••••••"
              />
              <button
                type="button"
                data-testid="login-toggle-password"
                onClick={() => setShowPassword((p) => !p)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-neutral-200 p-1"
              >
                {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </label>

          {error && (
            <div
              data-testid="login-error"
              className="mb-4 px-3 py-2 border border-red-700 bg-red-950/30 text-red-300 text-xs font-jet"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid="login-submit-button"
            className="btn-primary btn-glitch w-full"
          >
            {busy ? "Authenticating..." : "Authenticate"}
          </button>

          <div className="mt-6 flex items-center justify-between text-xs">
            <Link to="/" data-testid="login-back-home" className="text-neutral-500 hover:text-neutral-200 font-jet uppercase tracking-[0.18em]">
              ← Home
            </Link>
            <Link to="/register" data-testid="login-link-register" className="text-amber-400 hover:text-amber-300 font-jet uppercase tracking-[0.18em]">
              Create account →
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
